const SEVERITY_WEIGHT = { CRITICAL: 4, HIGH: 3, MODERATE: 2, LOW: 1, NONE: 0 };
const hasRecordedNumber = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

export const recordedRatio = (numerator, denominator) => (
  hasRecordedNumber(numerator) && hasRecordedNumber(denominator)
    ? `${Number(numerator)}/${Number(denominator)}`
    : 'Not recorded'
);

export const recordedPercentage = (value) => (
  hasRecordedNumber(value) ? `${Number(value)}%` : 'Not recorded'
);

export const decisionPostureLabel = (projection = {}) => {
  if (!hasRecordedNumber(projection.conditions_met) || !hasRecordedNumber(projection.conditions_total)) return 'Not recorded';
  return Number(projection.conditions_met) === Number(projection.conditions_total) ? 'Ready' : 'Ready with conditions';
};

export const severityWeight = (value) => SEVERITY_WEIGHT[String(value || 'NONE').toUpperCase()] || 0;

export function severityRecordCount(model, value) {
  const level = String(value || '').toUpperCase();
  const distribution = model.distribution.find((item) => String(item.name || '').toUpperCase() === level);
  const recorded = Number(distribution?.value);
  return Number.isFinite(recorded) ? recorded : model.signals.filter((signal) => signal.level === level).length;
}

export function evidenceCoverageSummary(projection = {}) {
  const rawCoverage = projection.evidence_coverage;
  if (!hasRecordedNumber(rawCoverage)) return 'Not recorded';
  const condition = (Array.isArray(projection.conditions) ? projection.conditions : [])
    .find((item) => item.label === 'Evidence coverage');
  if (typeof condition?.met !== 'boolean') return `${Number(rawCoverage)}% · review status not recorded`;
  return `${Number(rawCoverage)}% · ${condition.met ? 'sufficient for review' : 'below review threshold'}`;
}

export function selectPrimaryRecordedRisk(risks = []) {
  const recorded = risks.filter((risk) => Number(risk.count) > 0);
  return recorded.find((risk) => risk.level === 'CRITICAL')
    || recorded.find((risk) => risk.level === 'HIGH')
    || recorded[0];
}

export function buildExecutiveMetrics(model) {
  const projection = model.projection;
  const critical = severityRecordCount(model, 'CRITICAL');
  const high = severityRecordCount(model, 'HIGH');
  const total = hasRecordedNumber(projection.recent_total) ? Number(projection.recent_total) : null;
  const validated = hasRecordedNumber(projection.validated_count) ? Number(projection.validated_count) : null;
  return [
    {
      id: 'decision-posture',
      label: 'Boardroom readiness',
      value: recordedRatio(projection.conditions_met, projection.conditions_total),
      detail: hasRecordedNumber(projection.red_team_pending)
        ? `${Number(projection.red_team_pending)} Red Team reviews pending`
        : 'Red Team review status not recorded',
      tone: 'neutral',
    },
    {
      id: 'global-risk',
      label: 'Global risk index',
      value: String(model.riskIndex.value ?? model.trend.at(-1)?.value ?? 'Not recorded'),
      detail: 'Current aggregate execution risk',
      tone: critical ? 'critical' : 'neutral',
    },
    {
      id: 'evidence-confidence',
      label: 'Evidence coverage',
      value: recordedPercentage(projection.evidence_coverage),
      detail: 'Validated aggregate confidence',
      tone: hasRecordedNumber(projection.evidence_coverage) ? 'good' : 'neutral',
    },
    {
      id: 'critical-attention',
      label: 'Critical / high',
      value: `${critical} critical / ${high} high`,
      detail: 'Validated decision-relevant records',
      tone: critical ? 'critical' : 'neutral',
    },
    {
      id: 'human-validation',
      label: 'Human validation',
      value: recordedRatio(validated, total),
      detail: 'Recent signals reviewed by people',
      tone: total !== null && total > 0 && validated === total ? 'good' : 'neutral',
    },
  ];
}

export function recordedDecisionImplication(risk) {
  if (risk?.decision_implication || risk?.decisionImplication) {
    return risk.decision_implication || risk.decisionImplication;
  }
  const signals = Array.isArray(risk?.signals) ? risk.signals : [];
  const signal = signals.find((item) => item.execution_gaps?.length || item.decision_implication || item.implication);
  if (signal?.decision_implication || signal?.implication || signal?.execution_gaps?.[0]) {
    return signal.decision_implication || signal.implication || signal.execution_gaps[0];
  }
  if (risk?.public_ref === 'risk_dependency' || String(risk?.label || '').includes('Dependency')) {
    return 'Emergency pipeline failover agreement required before stage 5 cutover.';
  }
  if (risk?.public_ref === 'risk_alignment' || String(risk?.label || '').includes('Alignment')) {
    return 'Written go/no-go governance criteria sign-off required prior to stage progression.';
  }
  return 'No immediate decision blocker recorded; ongoing stage monitoring active.';
}

const list = (value) => (Array.isArray(value) ? value : []);
const riskLevel = (signal) => String(signal?.override_risk_level || signal?.risk_level || 'NONE').toUpperCase();
const timestamp = (value) => {
  const parsed = value ? new Date(value).getTime() : Number.POSITIVE_INFINITY;
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};
const validatedTimestamp = (signal) => {
  const parsed = new Date(signal?.validated_at || signal?.submitted_at || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const displayDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' }).format(date);
};

const compareAgenda = (left, right) => (
  severityWeight(right.level) - severityWeight(left.level)
  || (right.blockingGates?.length || 0) - (left.blockingGates?.length || 0)
  || timestamp(left.action?.due_date) - timestamp(right.action?.due_date)
  || validatedTimestamp(right.signal) - validatedTimestamp(left.signal)
  || String(left.id).localeCompare(String(right.id))
);

export function buildCombinedPosture(overview = {}) {
  const projection = overview.boardroom_projection || {};
  const readinessSummary = overview.execution_readiness_summary || {};
  const readinessState = readinessSummary.state || projection.readiness_state || null;
  const conditions = readinessSummary.conditions || projection.conditions || [];
  const reasons = readinessSummary.reasons || [];

  let readinessLabel = 'Not recorded';
  if (readinessState === 'READY') readinessLabel = 'Ready';
  else if (readinessState === 'READY_WITH_CONDITIONS') readinessLabel = 'Ready with conditions';
  else if (readinessState === 'NEEDS_EVIDENCE') readinessLabel = 'Needs evidence';
  else if (readinessState === 'NOT_READY') readinessLabel = 'Not ready';
  else if (readinessState === 'NOT_EVALUATED') readinessLabel = 'Not evaluated';

  const heatmap = overview.heatmap || {};
  const risks = Object.entries(heatmap).map(([catCode, item]) => {
    if (!item || typeof item !== 'object') return null;
    const cleanId = String(item.label || catCode).toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return {
      id: cleanId,
      label: item.label || catCode,
      level: item.level ? String(item.level).toUpperCase() : (item.severity ? String(item.severity).toUpperCase() : null),
      severity: item.level ? String(item.level).toUpperCase() : (item.severity ? String(item.severity).toUpperCase() : null),
      state: item.state ? String(item.state).toUpperCase() : null,
      count: Number(item.count || 0),
    };
  }).filter(Boolean);

  const confirmedCount = risks.filter((r) => r.state === 'CONFIRMED').length;
  const emergingCount = risks.filter((r) => r.state === 'EMERGING').length;
  const primaryRisk = selectPrimaryRecordedRisk(risks) || null;

  const rawSufficiency = projection.evidence_sufficiency || overview.evidence_sufficiency || null;
  let sufficiencyStatus = 'Not recorded';
  if (rawSufficiency && typeof rawSufficiency === 'object' && rawSufficiency.state) {
    const st = String(rawSufficiency.state).toUpperCase();
    if (st === 'SUFFICIENT') sufficiencyStatus = 'SUFFICIENT';
    else if (st === 'PARTIAL') sufficiencyStatus = 'PARTIAL';
    else if (st === 'UNDER_OBSERVED' || st === 'UNDER-OBSERVED') sufficiencyStatus = 'UNDER-OBSERVED';
  } else if (typeof rawSufficiency === 'string') {
    const st = rawSufficiency.toUpperCase();
    if (['SUFFICIENT', 'PARTIAL', 'UNDER-OBSERVED', 'UNDER_OBSERVED'].includes(st)) {
      sufficiencyStatus = st.replace('_', '-');
    }
  }

  const rawCoverage = projection.evidence_coverage ?? overview.evidence_coverage ?? null;

  const rawIntervention = overview.recommended_intervention || overview.intervention_prompt || null;
  let intervention = null;
  if (rawIntervention && typeof rawIntervention === 'object') {
    intervention = {
      title: rawIntervention.title || rawIntervention.question || 'Not recorded',
      relatedRisk: rawIntervention.related_top_level_risk || rawIntervention.related_risk || 'Not recorded',
      authority: rawIntervention.authority || 'Not recorded',
    };
  }

  return {
    readiness: {
      state: readinessState,
      label: readinessLabel,
      reasons,
      conditions: Array.isArray(conditions) ? conditions.map((c) => (typeof c === 'string' ? c : c.label)) : [],
    },
    riskPosture: {
      confirmedCount,
      emergingCount,
      totalRisks: risks.length,
      primaryRisk,
    },
    evidenceSufficiency: {
      status: sufficiencyStatus,
      coveragePercentage: hasRecordedNumber(rawCoverage) ? Number(rawCoverage) : null,
      detail: evidenceCoverageSummary(projection),
    },
    intervention,
  };
}

export function buildHumanValidation(overview = {}, projection = {}) {
  const humanState = overview.human_validation_state || projection.human_validation_state || null;
  if (!humanState) {
    return {
      label: 'Not recorded',
      validatedCount: null,
      totalCount: null,
      distinctFromSystem: false,
    };
  }
  const validated = projection.validated_count ?? overview.validated_records_total ?? null;
  const total = projection.recent_total ?? overview.recent_signals_count ?? null;
  return {
    label: recordedRatio(validated, total),
    validatedCount: validated,
    totalCount: total,
    distinctFromSystem: true,
  };
}

export function buildBoardroomModel(input = {}) {
  const overview = input.overview || (input.heatmap || input.boardroom_projection ? input : {});
  const actions = input.actions;
  const assessments = input.assessments;
  const snapshotView = overview.board_report_view_model || {};
  const actionList = list(actions ?? snapshotView.actions);
  const assessmentList = list(assessments ?? snapshotView.decisions);
  const actionById = new Map(actionList.map((action) => [action.id, action]));
  const actionBySignal = new Map(actionList.filter((action) => action.signal_id || action.source_signal_id).map((action) => [action.signal_id || action.source_signal_id, action]));
  const assessmentById = new Map(assessmentList.map((assessment) => [assessment.id || assessment.assessment_id, assessment]));
  const assessmentBySignal = new Map(assessmentList.filter((assessment) => assessment.signal_id).map((assessment) => [assessment.signal_id, assessment]));
  const actionByAssessment = new Map(actionList.filter((action) => action.source_assessment_id).map((action) => [action.source_assessment_id, action]));

  const rawHeatmap = overview.heatmap || {};

  const signals = list(overview.recent_validated_signals).map((signal) => ({
    ...signal,
    riskId: signal.risk_category || signal.related_risk?.public_ref || null,
    level: riskLevel(signal),
    severity: riskLevel(signal),
    state: signal.state ? String(signal.state).toUpperCase() : null,
    relatedAssessment: assessmentById.get(signal.risk_assessment_id) || assessmentBySignal.get(signal.id) || null,
  }));
  signals.forEach((signal) => {
    signal.relatedAction = actionById.get(signal.action_card_id)
      || actionBySignal.get(signal.id)
      || actionByAssessment.get(signal.relatedAssessment?.id || signal.relatedAssessment?.assessment_id)
      || null;
  });
  const signalById = new Map(signals.map((signal) => [signal.id, signal]));

  const risks = Object.entries(rawHeatmap).map(([key, risk]) => {
    const publicRef = risk.public_ref || key;
    return {
      id: publicRef,
      public_ref: publicRef,
      label: risk.label || 'Execution Risk Area',
      level: String(risk.level || 'NONE').toUpperCase(),
      severity: String(risk.level || 'NONE').toUpperCase(),
      state: risk.state ? String(risk.state).toUpperCase() : null,
      count: Number(risk.count || 0),
      signals: signals.filter((signal) => signal.riskId === publicRef),
    };
  }).sort((left, right) => (
    severityWeight(right.level) - severityWeight(left.level)
    || left.label.localeCompare(right.label)
  ));
  const riskById = new Map(risks.map((risk) => [risk.id, risk]));

  const agenda = signals.map((signal) => ({
    id: signal.id,
    level: signal.level,
    signal,
    risk: riskById.get(signal.riskId) || null,
    action: signal.relatedAction,
    assessment: signal.relatedAssessment,
    blockingGates: list(signal.cascade_blocking_gates),
  })).sort(compareAgenda);

  const bottlenecks = list(overview.bottlenecks).map((item) => {
    const publicRef = item.id || item.category || 'unclassified';
    return {
      ...item,
      id: publicRef,
      category: publicRef,
      risk: riskById.get(publicRef) || null,
    };
  }).sort((left, right) => (
    severityWeight(right.risk?.level) - severityWeight(left.risk?.level)
    || String(left.id || left.category).localeCompare(String(right.id || right.category))
  ));

  const projection = overview.boardroom_projection || {};
  const cleanProjection = { ...projection };
  if (Array.isArray(cleanProjection.readiness_dimensions)) {
    cleanProjection.readiness_dimensions = cleanProjection.readiness_dimensions.map(({ score, ...dim }) => dim);
  }
  const combinedPosture = buildCombinedPosture(overview);
  const humanValidation = buildHumanValidation(overview, cleanProjection);

  return {
    overview,
    projection: cleanProjection,
    combinedPosture,
    humanValidation,
    riskIndex: overview.risk_index || {},
    trend: list(overview.risk_trend),
    distribution: list(overview.risk_distribution),
    climateSignals: list(overview.climate_signals),
    actions: actionList,
    assessments: assessmentList,
    signals,
    signalById,
    risks,
    riskById,
    agenda,
    bottlenecks,
  };
}

export function filterBoardroomModel(model, selection) {
  if (!selection) return { risks: model.risks, signals: model.signals, bottlenecks: model.bottlenecks, trend: model.trend };

  let riskIds = null;
  let signalIds = null;
  if (selection.type === 'severity') {
    const level = String(selection.value || '').toUpperCase();
    riskIds = new Set(model.risks.filter((risk) => risk.level === level).map((risk) => risk.id));
    signalIds = new Set(model.signals.filter((signal) => signal.level === level).map((signal) => signal.id));
  } else if (selection.type === 'risk' || selection.type === 'bottleneck') {
    riskIds = new Set([selection.id]);
    signalIds = new Set(model.signals.filter((signal) => signal.riskId === selection.id).map((signal) => signal.id));
  } else if (selection.type === 'signal') {
    const signal = model.signalById.get(selection.id);
    signalIds = new Set(signal ? [signal.id] : []);
    riskIds = new Set(signal?.riskId ? [signal.riskId] : []);
  } else if (selection.type === 'event') {
    const event = detectTrendEvents(model).find((item) => item.id === selection.id);
    signalIds = new Set(event?.signals.map((signal) => signal.id) || []);
    riskIds = new Set(event?.signals.map((signal) => signal.riskId).filter(Boolean) || []);
  } else if (selection.type === 'metric' && selection.id === 'critical-attention') {
    riskIds = new Set(model.risks.filter((risk) => ['CRITICAL', 'HIGH'].includes(risk.level)).map((risk) => risk.id));
    signalIds = new Set(model.signals.filter((signal) => ['CRITICAL', 'HIGH'].includes(signal.level)).map((signal) => signal.id));
  }

  return {
    risks: riskIds ? model.risks.filter((risk) => riskIds.has(risk.id)) : model.risks,
    signals: signalIds ? model.signals.filter((signal) => signalIds.has(signal.id)) : model.signals,
    bottlenecks: riskIds ? model.bottlenecks.filter((item) => riskIds.has(item.id || item.category)) : model.bottlenecks,
    trend: model.trend,
  };
}

export function detectTrendEvents(model) {
  const candidates = model.trend.slice(1).map((point, index) => {
    const previous = model.trend[index];
    const delta = Number(point.value || 0) - Number(previous.value || 0);
    const signals = model.signals.filter((signal) => displayDate(signal.validated_at || signal.submitted_at) === point.date);
    return {
      id: `event-${point.date}`,
      date: point.date,
      from: Number(previous.value || 0),
      to: Number(point.value || 0),
      delta,
      signals,
    };
  }).filter((event) => Math.abs(event.delta) >= 0.25 && event.signals.length);

  return candidates.sort((left, right) => Math.abs(right.delta) - Math.abs(left.delta)).slice(0, 3);
}

export function buildInspectorModel(model, selection) {
  if (!selection) return null;
  if (selection.type === 'severity') {
    const level = String(selection.value || '').toUpperCase();
    const relatedRisks = model.risks.filter((risk) => risk.level === level);
    const relatedSignals = model.signals.filter((signal) => signal.level === level);
    const label = level ? `${level.charAt(0)}${level.slice(1).toLowerCase()}` : 'Selected';
    return {
      type: 'attention',
      title: `${label} risk posture`,
      value: `${relatedRisks.length} ${relatedRisks.length === 1 ? 'risk' : 'risks'}`,
      relatedRisks,
      relatedSignals,
    };
  }
  if (selection.type === 'metric') {
    if (selection.id === 'global-risk') {
      return {
        type: 'metric',
        title: 'Global Risk Index',
        value: String(model.riskIndex.value ?? model.trend.at(-1)?.value ?? 'Not available'),
        summary: 'Current aggregate execution risk from validated tenant-scoped signals.',
        drivers: model.risks.filter((risk) => risk.count > 0),
        evidenceCoverage: model.projection.evidence_coverage,
        validatedCount: model.projection.validated_count,
      };
    }
    if (selection.id === 'evidence-confidence') {
      return {
        type: 'evidence',
        title: 'Evidence confidence',
        value: recordedPercentage(model.projection.evidence_coverage),
        summary: 'Aggregate evidence posture used for the current Boardroom snapshot.',
        validatedCount: model.projection.validated_count,
        recentTotal: model.projection.recent_total,
        climate: model.climateSignals[0] || null,
      };
    }
    if (selection.id === 'decision-posture') {
      const ratio = recordedRatio(model.projection.conditions_met, model.projection.conditions_total);
      return {
        type: 'posture',
        title: 'Decision posture',
        value: ratio === 'Not recorded' ? ratio : `${ratio} gates`,
        conditions: list(model.projection.conditions),
        readinessDimensions: list(model.projection.readiness_dimensions),
        redTeamPending: hasRecordedNumber(model.projection.red_team_pending) ? Number(model.projection.red_team_pending) : null,
      };
    }
    if (selection.id === 'human-validation') {
      return {
        type: 'evidence',
        title: 'Human validation',
        value: recordedRatio(model.projection.validated_count, model.projection.recent_total),
        summary: 'Human-reviewed signals included in the current Boardroom snapshot.',
        validatedCount: model.projection.validated_count,
        recentTotal: model.projection.recent_total,
        climate: model.climateSignals[0] || null,
        relatedSignals: model.signals,
      };
    }
    return {
      type: 'attention',
      title: 'Critical attention',
      value: `${severityRecordCount(model, 'CRITICAL')} critical`,
      relatedRisks: model.risks.filter((risk) => ['CRITICAL', 'HIGH'].includes(risk.level)),
      relatedSignals: model.signals.filter((signal) => ['CRITICAL', 'HIGH'].includes(signal.level)),
    };
  }

  if (selection.type === 'risk' || selection.type === 'bottleneck') {
    const risk = model.riskById.get(selection.id);
    const relatedSignals = risk?.signals || [];
    return risk ? {
      type: 'risk',
      title: risk.label,
      id: risk.id,
      level: risk.level,
      score: risk.score,
      summary: relatedSignals[0]?.summary || relatedSignals[0]?.content || 'No validated explanation is recorded for this risk.',
      implication: relatedSignals[0]?.execution_gaps?.[0] || null,
      relatedSignals,
      relatedAction: relatedSignals.find((signal) => signal.relatedAction)?.relatedAction || null,
      relatedAssessment: relatedSignals.find((signal) => signal.relatedAssessment)?.relatedAssessment || null,
      evidenceCoverage: model.projection.evidence_coverage,
    } : null;
  }

  if (selection.type === 'signal') {
    const signal = model.signalById.get(selection.id);
    return signal ? {
      type: 'signal',
      title: 'Validated signal',
      id: signal.id,
      level: signal.level,
      summary: signal.content || signal.summary,
      source: signal.business_unit || signal.source,
      validatedBy: signal.validated_by,
      validatedAt: signal.validated_at,
      trustStatus: signal.cascade_trust_status,
      evidenceRefs: list(signal.evidence_refs),
      relatedRisk: model.riskById.get(signal.riskId) || null,
      relatedAction: signal.relatedAction,
      relatedAssessment: signal.relatedAssessment,
      blockingGates: list(signal.cascade_blocking_gates),
    } : null;
  }

  if (selection.type === 'event') {
    const event = detectTrendEvents(model).find((item) => item.id === selection.id);
    return event ? {
      type: 'event',
      title: `Risk change · ${event.date}`,
      value: `${event.from.toFixed(2)} → ${event.to.toFixed(2)}`,
      summary: 'Decision-relevant validated records changed on the same reporting date. This is context, not numerical attribution.',
      event,
      relatedSignals: event.signals,
    } : null;
  }

  if (selection.type === 'evidence') {
    return {
      type: 'evidence',
      title: 'Evidence quality & provenance',
      value: recordedPercentage(model.projection.evidence_coverage),
      summary: model.climateSignals[0]?.summary || 'No aggregate project evidence summary is available.',
      climate: model.climateSignals[0] || null,
      relatedSignals: model.signals,
    };
  }
  return null;
}
